import {
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import { css } from "@compiled/react";
//@ts-ignore
import { useInView } from "react-intersection-observer";

import { isReducedMotion } from "@atlaskit/motion";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { GalleryControls } from "@/libraries/design/components";
import { ColumnLayout } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_MAX_WIDTH_XL,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM,
    WCL_GRID_PADDING_XL
} from "@/libraries/design/foundations/grid/src/base";

export type GalleryContainerProps = PropsWithChildren;

const galleryStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "end",
    gap: token("space.400"),
    [media.above.md]: {
        gap: token("space.500")
    }
});

const scrollableStyles = css({
    // Aligns to the grid but bleeds beyond the end, inline direction
    "width": "100%",
    "overflowX": "auto",
    ["@supports (scrollbar-width: none)"]: {
        scrollbarWidth: "none"
    },
    "scrollBehavior": "smooth",
    "scrollSnapType": "x mandatory",
    "paddingInline": `var(--cl-overflow-bleed, 0px)`,
    "scrollPaddingInline": `var(--cl-overflow-bleed, 0px)`,
    "--cl-overflow-bleed": `${WCL_GRID_PADDING_SM}px`,
    [media.only.sm]: {
        "--cl-overflow-bleed": `${WCL_GRID_PADDING_LG}px`
    },
    [media.only.md]: {
        "--cl-overflow-bleed": `max(${WCL_GRID_PADDING_LG}px, calc((100% - ${
            WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2
        }px) / 2 + ${WCL_GRID_PADDING_LG}px))`
    },
    [media.above.lg]: {
        "--cl-overflow-bleed": `max(${WCL_GRID_PADDING_XL}px, calc((100% - ${
            WCL_GRID_MAX_WIDTH_XL + WCL_GRID_PADDING_XL * 2
        }px) / 2 + ${WCL_GRID_PADDING_XL}px))`
    }
});

// Applied when all content fits within the viewport — centers the inner row and removes bleed/scroll behaviour
const scrollableCenteredStyles = css({
    display: "flex",
    justifyContent: "center",
    paddingInline: 0,
    scrollPaddingInline: 0
});

const innerStyles = css({
    "display": "flex",
    "flexDirection": "row",
    "alignItems": "end",
    "width": "max-content",
    "--wcl-gallery-gap": token("space.400"),
    "gap": "var(--wcl-gallery-gap)",
    [media.above.md]: {
        "--wcl-gallery-gap": token("space.600")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > *": {
        scrollSnapAlign: "start"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > span": {
        "display": "inline-flex",
        "width": "0",
        "&:first-of-type": {
            marginInlineEnd: `calc(var(--wcl-gallery-gap) * -1)`
        },
        "&:last-of-type": {
            marginInlineStart: `calc(var(--wcl-gallery-gap) * -1)`
        }
    }
});

const SCROLL_BUFFER = 40;

export const GalleryContainer = ({ children }: GalleryContainerProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    // True when the inner content row fits entirely within the scroll container's visible width
    const [isFitting, setIsFitting] = useState(false);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        const innerEl = innerRef.current;
        if (!scrollEl || !innerEl) {
            return;
        }

        const observer = new ResizeObserver(() => {
            // clientWidth = visible width of the scroll container (excludes scrollbar)
            // scrollWidth of inner = natural width of all content
            setIsFitting(
                innerEl.scrollWidth <=
                    scrollEl.clientWidth - WCL_GRID_PADDING_SM * 2
            );
        });

        observer.observe(scrollEl);
        observer.observe(innerEl);

        return () => observer.disconnect();
    }, []);

    const { ref: firstItemRef, inView: isStartVisible } = useInView({
        root: scrollRef?.current,
        threshold: 1
    });
    const { ref: lastItemRef, inView: isEndVisible } = useInView({
        root: scrollRef?.current,
        threshold: 1
    });

    const handleChange = useCallback((direction: number) => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }

        const pageAmount = Math.min(WCL_GRID_MAX_WIDTH, el.clientWidth);

        el.scrollTo({
            top: 0,
            left: el.scrollLeft + (pageAmount - SCROLL_BUFFER) * direction,
            behavior: isReducedMotion() ? "auto" : "smooth"
        });
    }, []);

    return (
        <div css={galleryStyles}>
            <div
                css={[scrollableStyles, isFitting && scrollableCenteredStyles]}
                ref={scrollRef}
            >
                <div css={innerStyles} ref={innerRef}>
                    <span aria-hidden="true" ref={firstItemRef} />
                    {children}
                    <span aria-hidden="true" ref={lastItemRef} />
                </div>
            </div>
            {!isFitting && !(isStartVisible && isEndVisible) && (
                <ColumnLayout layout="12" horizontalAlignment="end">
                    <GalleryControls
                        isFirstItemVisible={isStartVisible}
                        isLastItemVisible={isEndVisible}
                        onPrevious={() => handleChange(-1)}
                        onNext={() => handleChange(1)}
                        size="large"
                    />
                </ColumnLayout>
            )}
        </div>
    );
};
